import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { debounce } from 'lodash';

ModuleRegistry.registerModules([AllCommunityModule]);

const AgGrid = ({ agGridRef: propsAgGridRef, style, width, rows, columns, showFooter, showFilter, rowSelection, defaultColDef, total, gridOptions: propsGridOptions = {}, domLayout }) => {
    const [localRows, setLocalRows] = useState([]);
    const agGridRef = propsAgGridRef || useRef(null);
    const maxHeight = window.innerHeight - 200;
    const [gridHeight, setGridHeight] = useState(maxHeight); 
    
    useEffect(() => {
        setLocalRows(rows);
        if (rows)
            calculateGridHeight(rows.length);
            adjustColumnWidth();
    }, [rows])

    // Adding Total row on the bottom
    useEffect(() => {
        if (agGridRef.current && agGridRef.current.api && rows && rows.length > 0 && total && total.length > 0){
            agGridRef.current.api.setGridOption("pinnedBottomRowData", total);
        }
    }, [agGridRef, total, rows]);

    // Column Settings
    useEffect(() => {
        if (agGridRef.current && agGridRef.current.api && defaultColDef){
            agGridRef.current.api.setGridOption("defaultColDef", defaultColDef);
        }
    }, [agGridRef, defaultColDef]);

    // Show Footer
    useEffect(() => {
        if (agGridRef.current && agGridRef.current.api && showFooter){
            agGridRef.current.api.setGridOption("statusBar", {
                statusPanels: [
                    { statusPanel: 'agTotalAndFilteredRowCountComponent' },
                    { statusPanel: 'agTotalRowCountComponent' },
                    { statusPanel: 'agFilteredRowCountComponent' },
                    { statusPanel: 'agSelectedRowCountComponent' },
                    { statusPanel: 'agAggregationComponent' }
                ]
            });
        }
    }, [agGridRef, showFooter])

    // Window Resize to adjust column width
    useEffect(() => {
        // Add the event listener for window resize
        window.addEventListener('resize', adjustColumnWidth);
        // // Clean up the event listener on component unmount
        return () => {
          window.removeEventListener('resize', adjustColumnWidth);
        };
    }, []);

    // Column Height related function
    const calculateGridHeight = useCallback((rowCount) => {
        // Calculate total grid height based on row count and row height
        const rowHeight = rowCount < 10 ? 51 : 45;
        const headerHeight = showFooter ? 50 : 30;
        const footerHeight = showFooter ? 30 : 10;
        const calculatedHeight = rowCount <= 1  ? showFooter ? 150 : 100 : 
                                rowCount < 15 && rowCount >= 2 ? 
                                (rowCount * rowHeight) + headerHeight + footerHeight : Math.min(rowCount * rowHeight, maxHeight);
        setGridHeight(calculatedHeight);
    }, [showFooter, showFilter, maxHeight]);

    // Column Width related functions
    const adjustColumnWidth = debounce(() => {
        if (agGridRef.current) {
            const totalColumnWidth = calculateTotalColumnWidth();
            const containerWidth = document.getElementById("agGridContainer").clientWidth;
            if (totalColumnWidth < containerWidth) {
                sizeColumnsToFitContent();
            } else {
                autoSizeAllColumns();
            }
        }
    }, 200);

    const autoSizeAllColumns = () => {
        if (agGridRef.current && agGridRef.current.api) {
          const allColumns = agGridRef.current.api.getColumns();
          agGridRef.current.api.autoSizeColumns(allColumns);
        }
      };
    
    const sizeColumnsToFitContent = () => {
        if (agGridRef.current && agGridRef.current.api) {
            const allColumns = agGridRef.current.api.getColumns();
            const containerWidth = document.getElementById("agGridContainer").clientWidth;``
            let totalWidth = 0;

            allColumns.forEach((col) => {
                totalWidth += col.actualWidth; // Get the actual width of each column
            });
            const scaleFactor = containerWidth / totalWidth;
            allColumns.forEach((col) => {
                const newWidth = col.getActualWidth() * scaleFactor;
                agGridRef.current.api.getColumnState().forEach((state) => {
                    if (state.colId === col.getColId()) {
                        state.width = newWidth;
                    }
                });
            });
            agGridRef.current.api.sizeColumnsToFit(); // Resize columns to fit grid width
        }
    };

    const calculateTotalColumnWidth = () => {
        if (agGridRef.current && agGridRef.current.api) {
            const allColumns = agGridRef.current.api.getColumns();
            // console.log(allColumns);
            let totalWidth = 0;

            allColumns.forEach((col) => {
                totalWidth += col.actualWidth; // Get the actual width of each column
            });
            // console.log(totalWidth);

            return totalWidth; // Store the total width in state
        }
    };

    const calculateTotal = (data) => {
        if (!Array.isArray(data) || data.length === 0) return [];
    
        const totals = {};
    
        // Loop through each row
        data.forEach(row => {
            Object.entries(row).slice(1).forEach(([key, value], index) => { // skip the first key, normally displaying "Total"
                // Sum only numeric fields (and ignore null/undefined)
                if (typeof value === 'number' && !isNaN(value)) {
                    totals[key] = (totals[key] || 0) + value;
                }
            });
        });
        const firstKey = Object.keys(data[0])[0];
        totals[firstKey] = "Total";
    
        return totals;
    };
    
    const onFilterChanged = () => {
        const filteredRows = [];
        if (agGridRef.current && agGridRef.current.api && showFilter) {
            agGridRef.current.api.forEachNodeAfterFilter((node) => {
                if (node.data) filteredRows.push(node.data);
            });
            const newTotal = calculateTotal(filteredRows);
            agGridRef.current.api.setGridOption("pinnedBottomRowData", [newTotal]);
            // setLocalTotal(newTotal);
        }else{
            return undefined;
        }
    };

    // Grid Ready
    const onGridReady = (params) => {
        adjustColumnWidth();
    };

    // **** Grid options ****
    const gridOptions = useMemo(() => ({
        ...propsGridOptions,
        suppressColumnMoveAnimation: true,
        suppressContextMenu: true,
        suppressColumnVirtualisation: true,
        suppressSetFilterByDefault: true,
        animateRows: true,
        columnDefs: columns,
        rowData: localRows,
        rowSelection,
        onGridReady,
        onFilterChanged,
        overlayNoRowsTemplate: '<b class="no-data-overlay">No data</b>'
    }), [propsGridOptions, columns, localRows, rowSelection]);

    return(
        <div id="agGridContainer" className={style === "gray" ? "grayHeader" : ""} style={{ height: `${gridHeight}px`, width: !width ? '100%' : width, marginBottom: 16 }}>
            <AgGridReact
                ref={agGridRef}
                rowModelType="clientSide"
                columnDefs={columns || []}
                rowData={localRows}
                gridOptions={gridOptions}
            />
            <div className="totalRows">{`Total: ${localRows ? localRows.length : 0} records`}</div>
        </div>
    )
};

export default AgGrid;